import type { Attachment } from 'stream-chat';

import { isScrapedContent, isUploadedAttachment } from './stream-attachment-types';

/**
 * Every field on a Stream attachment that a browser would fetch or navigate to. All of them are
 * written by whoever posted the message, straight to Stream's API, so none can be trusted as-is.
 */
export const ATTACHMENT_URL_FIELDS = [
  'asset_url',
  'image_url',
  'thumb_url',
  'title_link',
  'og_scrape_url',
  'author_link',
  'author_icon',
] as const satisfies ReadonlyArray<keyof Attachment>;

const WEB_PROTOCOLS = ['https:', 'http:'];

/**
 * A hostname as DNS resolves it: a trailing root dot names the same host, and the URL parser has
 * already folded the full-width and percent-encoded spellings of that dot into a plain `.`, so
 * stripping it here is what keeps `flow.example.` from slipping past a comparison with
 * `flow.example`.
 */
const canonicalHostname = (hostname: string): string => hostname.replace(/\.+$/, '');

/**
 * What `@braintree/sanitize-url` (the sanitizer stream-chat-react and stream-chat-react-native run
 * every address through before it reaches an `<img>` or `<Image>`) does to an address before
 * handing it to the browser: numeric HTML entities are decoded, `&newline;` and `&tab;` are
 * removed, and control, zero-width and BOM characters are stripped. Mirrors sanitize-url 6.0.4.
 *
 * The rules here parse the raw string, so an entity in the host was the way past them:
 * `https://flow.example&#47;api/x` parses with host `flow.example&`, which is public, then the
 * sanitizer turns `&#47;` into `/` and the browser fetches `https://flow.example/api/x`. The same
 * spelling reaches `127.0.0.1`. Nothing a genuine address needs is changed by this decode, so an
 * address the decode would change is simply refused rather than re-judged.
 *
 * sanitize-url writes the entity pattern as `/&#(\w+)(^\w|;)?/g`. Its `^\w` branch comes after
 * `&#\w+`, so it can never match, and `;?` below is the same pattern.
 */
const HTML_ENTITIES = /&#(\w+);?/g;
const HTML_CTRL_ENTITIES = /&(newline|tab);/gi;
// eslint-disable-next-line no-control-regex -- the sanitizer strips these, so the mirror must too
const CTRL_CHARACTERS = /[\u0000-\u001F\u007F-\u009F\u2000-\u200D\uFEFF]/gim;

const isChangedBySanitizer = (url: string): boolean =>
  url
    .replace(CTRL_CHARACTERS, '')
    .replace(HTML_ENTITIES, (_match, code: string) => String.fromCharCode(Number(code)))
    .replace(HTML_CTRL_ENTITIES, '')
    .replace(CTRL_CHARACTERS, '')
    .trim() !== url;

/**
 * Whether a dotted-quad IPv4 address is on the viewer's own machine or local network. The URL
 * parser has already folded the decimal, octal and hex spellings into this form.
 */
const isLocalIpv4 = (address: string): boolean => {
  const octets = address.split('.');
  if (octets.length !== 4 || !octets.every((octet) => /^\d+$/.test(octet))) return false;

  const [first, second] = octets.map(Number);
  return (
    first === 0 ||
    first === 10 ||
    first === 127 ||
    (first === 169 && second === 254) ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168)
  );
};

/**
 * The IPv4 address an IPv6 literal carries in its low 32 bits, or `null` when it carries none.
 *
 * Both prefixes that mean "this is really an IPv4 address" count: `::/96`, the deprecated
 * IPv4-compatible form, and `::ffff:0:0/96`, the IPv4-mapped form. The parser has already put the
 * address in its canonical form, so the groups are lower-case hex and at most one `::` compresses
 * a run of zeros.
 *
 * @param address - The literal without its brackets, as `URL.hostname` gives it.
 * @returns The address as a dotted quad, or `null`.
 */
const embeddedIpv4 = (address: string): string | null => {
  const [head, tail] = address.split('::');
  const groups =
    tail === undefined
      ? head.split(':')
      : [
          ...(head ? head.split(':') : []),
          ...Array(8 - countGroups(head) - countGroups(tail)).fill('0'),
          ...(tail ? tail.split(':') : []),
        ];

  if (groups.length !== 8 || !groups.every((group) => /^[0-9a-f]{1,4}$/.test(group))) return null;

  const words = groups.map((group) => parseInt(group, 16));
  const isIpv4Bearing =
    words.slice(0, 5).every((word) => word === 0) && (words[5] === 0 || words[5] === 0xffff);

  if (!isIpv4Bearing) return null;

  return [words[6] >> 8, words[6] & 0xff, words[7] >> 8, words[7] & 0xff].join('.');
};

const countGroups = (part: string): number => (part === '' ? 0 : part.split(':').length);

/**
 * Whether a hostname names the viewer's own machine or local network instead of a public site:
 * localhost, the IPv4 loopback, private, link-local and unspecified ranges, and the IPv6 loopback,
 * unique-local, link-local and IPv4-mapped forms. Rendering an image at such an address turns the
 * viewer's browser into a probe of their own network, and browsers exempt localhost from
 * mixed-content blocking, so the request would really go out. The URL parser has already folded
 * decimal, octal and hex IPv4 spellings into dotted form and keeps IPv6 literals in brackets.
 *
 * It reads the address, not DNS, so it stops a literal private address and nothing more: a name
 * like `cdn.example.test` whose A record points at 127.0.0.1 or 169.254.169.254 passes here and the
 * browser's request still goes wherever DNS sends it. Nothing in a browser can close that; treat
 * this as raising the cost of the literal attack, not as a network boundary.
 */
const isLocalNetworkHost = (hostname: string): boolean => {
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) return true;

  if (hostname.startsWith('[')) {
    const address = hostname.slice(1, -1);
    // An IPv6 literal can carry an IPv4 address in its low 32 bits, and the two spellings of that
    // are not interchangeable to a string check: the parser keeps the IPv4-mapped marker
    // (`::ffff:127.0.0.1` -> `::ffff:7f00:1`) but folds the deprecated IPv4-compatible form to bare
    // hex (`::127.0.0.1` -> `::7f00:1`). Matching on `::ffff:` therefore answered one spelling and
    // missed the other, so `[::169.254.169.254]` and `[::10.0.0.1]` read as public while
    // `[::ffff:8.8.8.8]` read as local. Decode the low 32 bits instead and judge the IPv4 address
    // itself, so every spelling of one address gets one answer.
    const embedded = embeddedIpv4(address);
    if (embedded !== null) return isLocalIpv4(embedded);

    return /^f[cd]/.test(address) || /^fe[89ab]/.test(address);
  }

  return isLocalIpv4(hostname);
};

/**
 * Whether an attachment address may be opened or fetched by this app.
 *
 * Accepts an absolute web address on a public host other than this app's own. A relative path or
 * an address on this host would make the viewer's browser call our own API on the sender's behalf,
 * which is what the 2026 penetration test did with a forged `image_url`; an address on the viewer's
 * own machine or network would make it probe that instead; non-web schemes such as `javascript:`
 * and `data:` are rejected with them, and so is any address the SDK's sanitizer would rewrite
 * before the browser saw it (see {@link isChangedBySanitizer}).
 *
 * This is the weaker of the two rules {@link hasOnlySafeUrls} applies, and it is only ever the
 * right one for an attachment that legitimately points off Stream's storage: a scraped link
 * preview or a Giphy card. A file, image, video, audio clip or voice recording is something
 * somebody uploaded, so it has to be on Stream's storage and is held to
 * {@link isStreamFileStorageUrl} instead. Applying this rule to an upload accepted any public
 * host, which let a forged `asset_url` render as a document and download under the sender's own
 * file name.
 *
 * @param url - The address as it came from Stream or from our API; may be missing.
 * @returns `true` when the address is safe to hand to the browser.
 */
export const isSafeAttachmentUrl = (url: string | null | undefined): boolean => {
  if (!url || isChangedBySanitizer(url)) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  const hostname = canonicalHostname(parsed.hostname);

  return (
    WEB_PROTOCOLS.includes(parsed.protocol) &&
    hostname !== canonicalHostname(window.location.hostname) &&
    !isLocalNetworkHost(hostname)
  );
};

/**
 * The site this app's auth cookie is scoped to: the last two labels of our own hostname, which is
 * the value `CookieConfiguration` sets as `Cookie.Domain` from `DashboardDomain` — `ipractice.nl`
 * for `flow.ipractice.nl`, `ipractice-test.nl` for the test hostnames. `null` on a single-label
 * host such as `localhost`, which has no siblings to worry about.
 */
const ownCookieSite = (): string | null => {
  const labels = canonicalHostname(window.location.hostname).split('.');

  return labels.length > 1 ? labels.slice(-2).join('.') : null;
};

/**
 * Whether an address is on this app's own site: our own host, or any sibling host under the domain
 * our auth cookie is scoped to.
 *
 * A sibling matters because the cookie is domain-scoped, so `client.ipractice.nl` is same-site with
 * `flow.ipractice.nl` and a request to it carries the psychologist's session whether `SameSite` is
 * `None` or `Strict`. Comparing one exact hostname therefore missed a whole class: it is the 2026
 * penetration test's finding 7 again, reached through a neighbour rather than through ourselves.
 */
const isOwnSiteHost = (hostname: string): boolean => {
  if (hostname === canonicalHostname(window.location.hostname)) return true;

  const site = ownCookieSite();

  return site !== null && (hostname === site || hostname.endsWith(`.${site}`));
};

/**
 * Whether an address is safe for something the renderer fetches without being asked, on an
 * attachment that legitimately points off Stream's storage.
 *
 * Stricter than {@link isSafeAttachmentUrl} in one way — no host on our own site, so the fetch can
 * never carry the reader's session — and no stricter in any other, so a genuine preview image on
 * `i.ytimg.com` or `lh3.googleusercontent.com` still renders.
 *
 * @param url - The address as it came from Stream; may be missing.
 * @returns `true` when the address is safe to fetch on render.
 */
export const isOffSiteAttachmentUrl = (url: string | null | undefined): boolean => {
  if (!url || !isSafeAttachmentUrl(url)) return false;

  return !isOwnSiteHost(canonicalHostname(new URL(url).hostname));
};

/** Mirrors `StreamAttachmentUrls.StreamFileStorageHostSuffix` on the server. */
const STREAM_FILE_STORAGE_HOST_SUFFIX = '.stream-io-cdn.com';

/**
 * Whether an address is an uploaded file on Stream's own storage: HTTPS on a
 * `<region>.stream-io-cdn.com` host, which is where every genuine upload comes back from, whether
 * it was sent from Flow, from a client app or by our own server.
 *
 * The same rule as the server's, deliberately, so the message list and the shared-files list agree
 * about what counts as a document we hold. The suffix carries its leading dot, so the apex is
 * rejected along with a look-alike such as `evil-stream-io-cdn.com`; the comparison is on the
 * parsed hostname, so userinfo cannot dress an attacker host up as this one, and an IDN look-alike
 * arrives punycoded and cannot match an ASCII suffix.
 *
 * @param url - The address as it came from Stream; may be missing.
 * @returns `true` only for `https://<region>.stream-io-cdn.com/...`.
 */
export const isStreamFileStorageUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  return (
    parsed.protocol === 'https:' &&
    canonicalHostname(parsed.hostname).endsWith(STREAM_FILE_STORAGE_HOST_SUFFIX)
  );
};

type GiphyData = NonNullable<Attachment['giphy']>;

/**
 * The rendition Stream's Card reads for a Giphy card: its `giphyVersion` default, which our
 * Channel does not override. That absence is what makes this constant right, and nothing in the
 * type system says so: setting the prop on our Channel without changing this would have the guard
 * validate a rendition the Card never reads.
 */
const GIPHY_RENDITION_READ_BY_CARD = 'fixed_height';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Whether a Giphy payload has the shape Stream's Card expects. The Card reads
 * `giphy[version].url` without a guard of its own, so a sender who posts `giphy: null`, a
 * rendition that is not an object, or a payload without the rendition it reads makes the render
 * throw; with no error boundary above it, that unmounts the whole app for everyone who opens the
 * conversation, and the message stays.
 */
const isWellFormedGiphy = (giphy: unknown): giphy is GiphyData =>
  isPlainObject(giphy) &&
  GIPHY_RENDITION_READ_BY_CARD in giphy &&
  Object.values(giphy).every(
    (rendition) => isPlainObject(rendition) && typeof rendition.url === 'string'
  );

/**
 * The image addresses nested under `giphy`: Stream's Card renders `giphy[version].url` as the
 * image of a Giphy card, so they are a sink the flat fields do not cover.
 */
const giphyUrls = (attachment: Attachment): string[] =>
  isWellFormedGiphy(attachment.giphy)
    ? Object.values(attachment.giphy).map((rendition) => rendition.url)
    : [];

/**
 * Which rule one address on an attachment has to pass, decided from the container the renderer will
 * draw and from the field the address sits in.
 *
 * An upload has to be on Stream's storage: that is where a genuine file, image, video, audio clip
 * or voice recording comes back from, and those are the containers that fetch an address on sight
 * or offer it as a download. A scraped link preview or a Giphy card legitimately lives on a
 * third-party host — a YouTube card's `thumb_url` really is on `i.ytimg.com` — so it only has to
 * clear the weaker rule.
 *
 * Which is why the rule takes a field as well, because a card's own addresses are not all alike and
 * being a card is only two sender-written fields away (`og_scrape_url`, `title_link`):
 *
 * - `asset_url` on a card is held to Stream's storage like an upload. Stream's own `Card` hands it
 *   on a `video` or `audio` card to `ReactPlayer`, which renders `<video preload="auto">` and
 *   downloads with no click, and no card Stream scrapes for us carries one — so this costs nothing.
 * - `image_url`, `thumb_url` and `author_icon` are the card's pictures, fetched on render. They may
 *   be third-party, so they take {@link isOffSiteAttachmentUrl}: anything except our own site. A
 *   genuine YouTube or Meet thumbnail passes; one aimed at a sibling host under our cookie domain
 *   does not, and that is the credentialed fetch the own-host comparison alone never saw.
 * - `title_link`, `og_scrape_url` and `author_link` are followed only on a click, and a
 *   questionnaire card's link points at `client.ipractice.nl` on purpose, so they stay on the
 *   weaker rule where our own site is allowed.
 *
 * The order mirrors `getAttachmentType`: scraped content is decided first, because
 * `isFileAttachment` matches anything carrying a `mime_type` and would otherwise claim a card.
 */
/** The fields the renderer fetches on sight, rather than only when the reader clicks. */
const FETCHED_FIELDS: ReadonlySet<string> = new Set(['image_url', 'thumb_url', 'author_icon']);

const addressRuleFor = (
  attachment: Attachment,
  field: (typeof ATTACHMENT_URL_FIELDS)[number]
): ((url: string | null | undefined) => boolean) => {
  if (isScrapedContent(attachment) || attachment.type === 'giphy') {
    if (field === 'asset_url') return isStreamFileStorageUrl;

    return FETCHED_FIELDS.has(field) ? isOffSiteAttachmentUrl : isSafeAttachmentUrl;
  }

  if (isUploadedAttachment(attachment)) return isStreamFileStorageUrl;

  // Anything else -- an unknown type, or Stream's `imgur` -- is drawn as a card on mobile and as a
  // bare title on the web, and the card fetches its pictures on sight, so those take the same
  // off-site rule a scraped card's pictures take.
  return FETCHED_FIELDS.has(field) ? isOffSiteAttachmentUrl : isSafeAttachmentUrl;
};

/**
 * Whether the attachment may be rendered: every address on it passes the rule for its kind and its
 * field (see {@link addressRuleFor}), and a Giphy card carries a payload Stream's Card can render.
 *
 * An attachment that fails must not be rendered at all. The image, video and PDF containers fetch
 * their address on sight; the file and PDF containers offer it as a download under the sender's own
 * file name; and Stream's own `Card` does both — it renders `image_url`, `thumb_url` and
 * `author_icon` as plain images, and hands `asset_url` on a `video` or `audio` card to
 * `ReactPlayer`.
 *
 * The answer is all-or-nothing by design, and that is what bounds how strict a card can be held:
 * hold a card's `thumb_url` to Stream's storage and a genuine YouTube preview does not lose its
 * image, it disappears from the conversation altogether. Holding those fields off our own site
 * instead is what closes the credentialed fetch without costing anything genuine — no address a
 * real card carries is ever on our own site.
 *
 * What that leaves, deliberately: a card's picture may still sit on a third-party host the sender
 * chose, so fetching it on render is a signal to that host that the conversation was opened. That
 * is inherent to showing link previews at all — Stream scrapes arbitrary sites — and removing it
 * means cards with no pictures, which is a product call rather than a security one.
 *
 * @param attachment - The attachment as Stream returned it on the message.
 * @returns `true` when the attachment carries no address, or only ones that field may point at.
 */
export const hasOnlySafeUrls = (attachment: Attachment): boolean => {
  if (
    attachment.type === 'giphy' &&
    attachment.giphy !== undefined &&
    !isWellFormedGiphy(attachment.giphy)
  ) {
    return false;
  }

  const fieldsPass = ATTACHMENT_URL_FIELDS.every((field) => {
    const url = attachment[field];

    return !url || addressRuleFor(attachment, field)(url);
  });

  // A Giphy rendition is the card's own image, third-party by definition, so it takes the rule the
  // card itself takes; there is no upload behind it to hold to Stream's storage.
  const giphyRule = addressRuleFor(attachment, 'image_url');

  return fieldsPass && giphyUrls(attachment).every((url) => !url || giphyRule(url));
};
